import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Radio, Select, Spin, Table, Tooltip, Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { CopyOutlined, DeleteOutlined, DownloadOutlined, FileExcelOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { decode as decodeHtml } from 'he';

import { storageItemApi } from '../../../../../apis';
import {
  BulkCreateSpecialHandlingFeeDto,
  BulkSpecialHandlingFeeResult,
  BulkSpecialHandlingFeeResultRow,
  SpecialHandlingFeeImportResult,
  SpecialHandlingFeeImportRow,
  StorageItem,
} from '../../../../../apis/client-axios';
import CustomInput from '../../../../../components/input/CustomInput';
import { CustomCommonButton } from '../../../../../components/buttons/common/CustomButton';
import NotificationError from '../../../../../components/notifications/NotificationError';
import './index.scss';

/**
 * Task 5/6 — 手数料一括追加 (商品管理 → 一括操作).
 * Bảng nhiều dòng, mỗi dòng là một cặp (item, khoản phí) ĐỘC LẬP — cùng một item được phép lặp ở
 * nhiều dòng với khoản phí khác nhau (item1/phí1, item1/phí2, item2/phí3).
 * Mỗi dòng = 1 charge PayJP riêng (BE chạy tuần tự, không dừng khi 1 dòng lỗi).
 * 3 bước trong cùng modal: edit → confirm → result.
 *
 * mode='import' (手数料一括インポート): thêm bước `upload` đứng trước — BE đọc file .xlsx + dò SKU (KHÔNG charge),
 * kết quả đổ vào CHÍNH bảng nhập ở bước edit để admin soát/sửa dòng lỗi, rồi thu như luồng nhập tay.
 */

type FeeRow = {
  key: number;
  item: StorageItem | null;
  title: string;
  chargeAmount: string;
  /** null = chưa chọn (dòng import có 粗利区分 trống/sai) — bắt admin chọn, không tự gán */
  isProfit: boolean | null;
  /** Số dòng trong file Excel (chỉ có ở dòng import) */
  sourceRow?: number;
  /** SKU ghi trong file — dùng làm từ khoá tìm sẵn khi SKU không khớp / khớp nhiều item */
  sku?: string;
};

type RowError = {
  item: boolean;
  title: boolean;
  chargeAmount: boolean;
  isProfit: boolean;
  /** Thông điệp riêng cho ô 商品 (SKU không thấy / trùng) thay cho câu mặc định */
  itemMessage?: string;
};

type Stage = 'upload' | 'edit' | 'confirm' | 'result';

type Props = {
  isOpen: boolean;
  /** 'manual' = 手数料一括追加 (mặc định) · 'import' = 手数料一括インポート (bắt đầu bằng bước tải file) */
  mode?: 'manual' | 'import';
  onClose: () => void;
  /** Gọi khi có ít nhất 1 dòng charge thành công (để refetch danh sách). */
  onSuccess?: () => void;
};

const ERROR_JP: Record<string, string> = {
  NO_PAYMENT_METHOD: '支払い方法が登録されていません',
  CHARGE_MONEY_FAILED: '決済に失敗しました',
  STORAGE_ITEM_NOT_FOUND: '商品が見つかりません',
  TITLE_REQUIRED: '手数料名が未入力です',
  CHARGE_AMOUNT_MUST_BE_POSITIVE: '金額が無効です',
};

const errorToJp = (code?: string) => (code ? ERROR_JP[code] ?? code : '');

/** Lỗi cấp FILE của bước import (BE ném 400 với mã này). */
const IMPORT_FILE_ERROR_JP: Record<string, string> = {
  FILE_REQUIRED: 'ファイルを選択してください。',
  FILE_TOO_LARGE: 'ファイルサイズが大きすぎます（2MBまで）。',
  FILE_FORMAT_NOT_SUPPORTED: 'Excelファイル（.xlsx）のみ対応しています。',
  FILE_FORMAT_INVALID: 'ファイルを読み込めませんでした。.xlsx形式か確認してください。',
  HEADER_INVALID: '見出し行（商品・手数料名・金額 ($)・粗利区分）が見つかりません。テンプレートをご利用ください。',
  NO_DATA_ROWS: 'データ行がありません。',
  TOO_MANY_LINES: '一度に読み込めるのは100行までです。',
};

const importErrorMessage = (err: any) => {
  if (err?.response?.status === 413) return IMPORT_FILE_ERROR_JP.FILE_TOO_LARGE;
  const body = JSON.stringify(err?.response?.data ?? '');
  const code = Object.keys(IMPORT_FILE_ERROR_JP).find((c) => body.includes(c));
  return code ? IMPORT_FILE_ERROR_JP[code] : 'ファイルの読み込みに失敗しました。';
};

const TEMPLATE_URL = `${process.env.PUBLIC_URL ?? ''}/templates/bulk-fee-template.xlsx`;

/** Dòng đọc từ file → dòng của bảng nhập + lỗi tương ứng. */
function importRowToFeeRow(r: SpecialHandlingFeeImportRow, key: number): { row: FeeRow; error?: RowError } {
  const row: FeeRow = {
    key,
    item: (r.item as unknown as StorageItem) ?? null,
    title: r.title ?? '',
    chargeAmount: r.chargeAmount !== null && r.chargeAmount !== undefined ? String(r.chargeAmount) : '',
    isProfit: r.isProfit ?? null,
    sourceRow: r.rowNo,
    sku: r.sku,
  };
  if (!r.errors?.length) return { row };
  const has = (c: string) => r.errors.includes(c);
  const itemMessage = has('SKU_REQUIRED')
    ? '商品（BayPack管理番号）が未入力です。'
    : has('SKU_NOT_FOUND')
      ? `「${r.sku}」の商品が見つかりません。`
      : has('SKU_DUPLICATED')
        ? `「${r.sku}」に該当する商品が${r.matchCount}件あります。選択してください。`
        : undefined;
  return {
    row,
    error: {
      item: !!itemMessage,
      itemMessage,
      title: has('TITLE_REQUIRED'),
      chargeAmount: has('CHARGE_AMOUNT_INVALID'),
      isProfit: has('IS_PROFIT_INVALID'),
    },
  };
}

const SEARCH_PAGE_SIZE = 20;

const emptyRow = (key: number): FeeRow => ({ key, item: null, title: '', chargeAmount: '', isProfit: true });

const itemLabel = (item: StorageItem) => `${item.internalSku ?? ''}｜${decodeHtml(item.itemName || '')}`;

/**
 * Ô chọn item của một dòng. Mỗi ô tự tìm kiếm riêng; `queryKey` chỉ phụ thuộc từ khoá nên các ô
 * gõ cùng từ khoá dùng chung cache. Chỉ gọi API khi dropdown mở (tránh N request lúc mới mở modal).
 */
const ItemSelectCell: React.FC<{
  value: StorageItem | null;
  hasError?: boolean;
  /** Từ khoá tìm sẵn khi mở dropdown (SKU của dòng import) — để admin chọn nhanh khi SKU trùng */
  defaultSearch?: string;
  onChange: (item: StorageItem) => void;
}> = ({ value, hasError, defaultSearch, onChange }) => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState(defaultSearch ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(defaultSearch?.trim() ?? '');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText.trim()), 400);
    return () => clearTimeout(t);
  }, [searchText]);

  const { data, isFetching } = useQuery({
    queryKey: ['bulkSpecialFee', 'searchItems', debouncedSearch],
    queryFn: () =>
      storageItemApi.storageItemControllerItemManagementAdmin(
        1,
        SEARCH_PAGE_SIZE,
        null,
        debouncedSearch || undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        'ALL',
      ),
    enabled: open,
    staleTime: 5000,
  });

  const options = useMemo(() => {
    const results = (data?.data?.content as StorageItem[]) ?? [];
    // Item đang chọn luôn có mặt để Select hiển thị đúng nhãn kể cả khi nó không nằm trong kết quả tìm
    const merged = value && !results.some((r) => r.id === value.id) ? [value, ...results] : results;
    return merged.map((item) => ({
      value: item.id,
      label: itemLabel(item),
      customer: `${item.storageRequest?.user?.name ?? ''} ${item.storageRequest?.user?.userCode ?? ''}`.trim(),
      item,
    }));
  }, [data, value]);

  return (
    <Select
      className="bulk-special-fee__item-select"
      showSearch
      filterOption={false}
      placeholder="SKU・商品名で検索"
      status={hasError ? 'error' : undefined}
      value={value?.id}
      options={options}
      onSearch={setSearchText}
      onDropdownVisibleChange={setOpen}
      onSelect={(id) => {
        const picked = options.find((o) => o.value === Number(id));
        if (picked) onChange(picked.item);
      }}
      optionRender={(option) => (
        <div className="bulk-special-fee__option">
          <span className="bulk-special-fee__option-main">{option.data.label}</span>
          {option.data.customer && <span className="bulk-special-fee__option-sub">{option.data.customer}</span>}
        </div>
      )}
      notFoundContent={isFetching ? <Spin size="small" /> : '該当する商品がありません'}
      popupClassName="bulk-special-fee__dropdown"
      popupMatchSelectWidth={420}
    />
  );
};

export const BulkSpecialFeeModal: React.FC<Props> = ({ isOpen, mode = 'manual', onClose, onSuccess }) => {
  const intl = useIntl();
  const isImport = mode === 'import';
  const modalTitle = isImport ? '手数料一括インポート' : '手数料一括追加';

  const [stage, setStage] = useState<Stage>(isImport ? 'upload' : 'edit');
  const [rows, setRows] = useState<FeeRow[]>(isImport ? [] : [emptyRow(1)]);
  const [rowErrors, setRowErrors] = useState<Record<number, RowError>>({});
  const [result, setResult] = useState<BulkSpecialHandlingFeeResult | null>(null);
  const [nextKey, setNextKey] = useState(2);
  const [uploadFile, setUploadFile] = useState<UploadFile | null>(null);
  const [importSummary, setImportSummary] = useState<{ total: number; errorRowCount: number } | null>(null);

  useEffect(() => {
    if (isOpen) {
      setStage(isImport ? 'upload' : 'edit');
      setRows(isImport ? [] : [emptyRow(1)]);
      setRowErrors({});
      setResult(null);
      setNextKey(2);
      setUploadFile(null);
      setImportSummary(null);
    }
  }, [isOpen, isImport]);

  const importMutation = useMutation({
    mutationFn: (file: File) => storageItemApi.storageItemControllerParseSpecialHandlingFeeFile(file),
    onSuccess: ({ data }: { data: SpecialHandlingFeeImportResult }) => {
      const errors: Record<number, RowError> = {};
      const mapped = (data?.rows ?? []).map((r, i) => {
        const { row, error } = importRowToFeeRow(r, i + 1);
        if (error) errors[row.key] = error;
        return row;
      });
      setRows(mapped);
      setRowErrors(errors);
      setNextKey(mapped.length + 1);
      setImportSummary({ total: data?.total ?? mapped.length, errorRowCount: data?.errorRowCount ?? 0 });
      setStage('edit');
    },
    onError: (err: any) => NotificationError({ content: importErrorMessage(err) }),
  });

  const handleImport = () => {
    const file = (uploadFile?.originFileObj ?? uploadFile) as unknown as File;
    if (!file || importMutation.isPending) return;
    importMutation.mutate(file);
  };

  const updateRow = (key: number, patch: Partial<FeeRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    setRowErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev[key] };
      if (patch.item !== undefined) {
        next.item = false;
        next.itemMessage = undefined;
      }
      if (patch.isProfit !== undefined) next.isProfit = false;
      if (patch.title !== undefined) next.title = false;
      if (patch.chargeAmount !== undefined) next.chargeAmount = false;
      return { ...prev, [key]: next };
    });
  };

  const addRow = () => {
    setRows((prev) => [...prev, emptyRow(nextKey)]);
    setNextKey((k) => k + 1);
  };

  /** Nhân bản dòng: chèn bản sao ngay dưới dòng gốc — nhập nhanh nhiều khoản phí cho cùng một item. */
  const duplicateRow = (key: number) => {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.key === key);
      if (idx < 0) return prev;
      const copy: FeeRow = { ...prev[idx], key: nextKey };
      return [...prev.slice(0, idx + 1), copy, ...prev.slice(idx + 1)];
    });
    setNextKey((k) => k + 1);
  };

  const removeRow = (key: number) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const grandTotal = useMemo(
    () => rows.reduce((sum, r) => sum + (Number.isFinite(Number(r.chargeAmount)) ? Number(r.chargeAmount) : 0), 0),
    [rows],
  );

  const validateRows = () => {
    const errors: Record<number, RowError> = {};
    let ok = true;
    for (const r of rows) {
      const amount = Number(r.chargeAmount.trim());
      const e: RowError = {
        item: !r.item,
        // giữ thông điệp SKU của dòng import nếu admin chưa chọn lại item
        itemMessage: !r.item ? rowErrors[r.key]?.itemMessage : undefined,
        title: !r.title.trim(),
        chargeAmount: !r.chargeAmount.trim() || !Number.isFinite(amount) || amount <= 0,
        isProfit: r.isProfit === null,
      };
      if (e.item || e.title || e.chargeAmount || e.isProfit) {
        ok = false;
        errors[r.key] = e;
      }
    }
    setRowErrors(errors);
    return ok;
  };

  const handleGoConfirm = () => {
    if (!validateRows()) {
      NotificationError({ content: '未入力の項目があります。' });
      return;
    }
    setStage('confirm');
  };

  const bulkMutation = useMutation({
    mutationFn: (dto: BulkCreateSpecialHandlingFeeDto) =>
      storageItemApi.storageItemControllerAddSpecialHandlingFeeBulk(dto),
    onSuccess: ({ data }) => {
      setResult(data);
      setStage('result');
      if ((data?.succeeded ?? 0) > 0) onSuccess?.();
    },
    onError: () => {
      NotificationError({ content: '登録に失敗しました。' });
      setStage('edit');
    },
  });

  const handleSubmit = () => {
    if (bulkMutation.isPending) return;
    bulkMutation.mutate({
      lines: rows.map((r) => ({
        storageItemId: r.item!.id,
        title: r.title.trim(),
        chargeAmount: Number(r.chargeAmount.trim()),
        isProfit: r.isProfit as boolean,
      })),
    });
  };

  const handleClose = () => {
    if (bulkMutation.isPending || importMutation.isPending) return;
    onClose();
  };

  // ───────────────────────── columns ─────────────────────────
  const editColumns = [
    {
      // Dòng import hiện số dòng Excel để admin dò lại trong file; nhập tay thì đánh số thứ tự
      title: isImport ? 'Excel行' : 'No.',
      key: 'no',
      width: isImport ? 72 : 56,
      render: (_: unknown, row: FeeRow, index: number) => (
        <span className="bulk-special-fee__row-no">{row.sourceRow ?? index + 1}</span>
      ),
    },
    {
      title: (
        <span>
          商品<span className="bulk-special-fee__required">*</span>
        </span>
      ),
      key: 'item',
      width: 320,
      render: (_: unknown, row: FeeRow) => (
        <div className="bulk-special-fee__field">
          <ItemSelectCell
            value={row.item}
            hasError={rowErrors[row.key]?.item}
            defaultSearch={row.sku}
            onChange={(item) => updateRow(row.key, { item })}
          />
          {rowErrors[row.key]?.item && (
            <span className="bulk-special-fee__error">
              {rowErrors[row.key]?.itemMessage ?? '商品を選択してください。'}
            </span>
          )}
        </div>
      ),
    },
    {
      title: (
        <span>
          手数料名<span className="bulk-special-fee__required">*</span>
        </span>
      ),
      key: 'title',
      render: (_: unknown, row: FeeRow) => (
        <div className="bulk-special-fee__field">
          <CustomInput
            key={`title-${row.key}`}
            placeholder="例: 梱包手数料"
            height={40}
            status={rowErrors[row.key]?.title ? 'error' : undefined}
            value={row.title}
            onChange={(e) => updateRow(row.key, { title: e.target.value })}
          />
          {rowErrors[row.key]?.title && <span className="bulk-special-fee__error">手数料名を入力してください。</span>}
        </div>
      ),
    },
    {
      title: (
        <span>
          金額 ($)<span className="bulk-special-fee__required">*</span>
        </span>
      ),
      key: 'chargeAmount',
      width: 140,
      render: (_: unknown, row: FeeRow) => (
        <div className="bulk-special-fee__field">
          <CustomInput
            key={`amount-${row.key}`}
            type="number"
            placeholder="0.00"
            height={40}
            min={0}
            status={rowErrors[row.key]?.chargeAmount ? 'error' : undefined}
            value={row.chargeAmount}
            onKeyDown={(e) => (['e', 'E', '+', '-'].includes(e.key) ? e.preventDefault() : null)}
            onChange={(e) => updateRow(row.key, { chargeAmount: e.target.value })}
          />
          {rowErrors[row.key]?.chargeAmount && (
            <span className="bulk-special-fee__error">金額を入力してください。</span>
          )}
        </div>
      ),
    },
    {
      title: '粗利区分',
      key: 'isProfit',
      width: 250,
      render: (_: unknown, row: FeeRow) => (
        <div className="bulk-special-fee__field">
          <Radio.Group value={row.isProfit} onChange={(e) => updateRow(row.key, { isProfit: e.target.value })}>
            <Radio value={true}>粗利対象</Radio>
            <Radio value={false}>粗利対象外</Radio>
          </Radio.Group>
          {rowErrors[row.key]?.isProfit && (
            <span className="bulk-special-fee__error">粗利区分を選択してください。</span>
          )}
        </div>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_: unknown, row: FeeRow) => (
        <div className="bulk-special-fee__row-actions">
          <Tooltip title="この行を複製">
            <button type="button" className="bulk-special-fee__icon-btn" onClick={() => duplicateRow(row.key)}>
              <CopyOutlined />
            </button>
          </Tooltip>
          <Tooltip title="この行を削除">
            <button
              type="button"
              className="bulk-special-fee__icon-btn is-danger"
              disabled={rows.length <= 1}
              onClick={() => removeRow(row.key)}
            >
              <DeleteOutlined />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const confirmColumns = [
    {
      // Không đặt width → cột 商品 giãn chiếm phần còn lại, tên hàng dài vẫn đọc được
      title: '商品',
      key: 'item',
      render: (_: unknown, row: FeeRow) => <span className="text-2-line">{row.item ? itemLabel(row.item) : ''}</span>,
    },
    { title: '手数料名', dataIndex: 'title', key: 'title', width: 260 },
    {
      title: '金額',
      key: 'chargeAmount',
      width: 120,
      render: (_: unknown, row: FeeRow) => `$${Number(row.chargeAmount).toFixed(2)}`,
    },
    {
      title: '粗利区分',
      key: 'isProfit',
      width: 120,
      render: (_: unknown, row: FeeRow) => (row.isProfit ? '粗利対象' : '粗利対象外'),
    },
  ];

  const resultColumns = [
    {
      title: '結果',
      key: 'success',
      width: 80,
      render: (_: unknown, r: BulkSpecialHandlingFeeResultRow) => (
        <span className={`bulk-special-fee__result-tag ${r.success ? 'is-ok' : 'is-ng'}`}>
          {r.success ? '成功' : '失敗'}
        </span>
      ),
    },
    { title: 'BayPack管理番号', dataIndex: 'internalSku', key: 'internalSku', width: 180 },
    {
      title: intl.formatMessage({ id: 'item.productName' }),
      dataIndex: 'itemName',
      key: 'itemName',
      render: (v: string) => <span className="text-2-line">{decodeHtml(v || '')}</span>,
    },
    { title: '手数料名', dataIndex: 'title', key: 'title', width: 160 },
    {
      title: '金額',
      dataIndex: 'chargeAmount',
      key: 'chargeAmount',
      width: 100,
      render: (v: number) => `$${Number(v ?? 0).toFixed(2)}`,
    },
    {
      title: '粗利区分',
      dataIndex: 'isProfit',
      key: 'isProfit',
      width: 100,
      render: (v: boolean) => (v ? '粗利対象' : '粗利対象外'),
    },
    {
      title: 'エラー',
      dataIndex: 'error',
      key: 'error',
      width: 200,
      render: (v: string) => <span className="bulk-special-fee__error-text">{errorToJp(v)}</span>,
    },
  ];

  const summaryText = `合計 ${rows.length}件　$${grandTotal.toFixed(2)}`;

  // ───────────────────────── render ─────────────────────────
  return (
    <Modal
      title=""
      closable={false}
      maskClosable={false}
      open={isOpen}
      onCancel={handleClose}
      className="bulk-special-fee-modal"
      centered
      destroyOnClose
      footer={null}
      width={1200}
    >
      {stage === 'upload' && (
        <>
          <p className="bulk-special-fee__title">{modalTitle}</p>
          <p className="bulk-special-fee__hint">
            テンプレートに記入した Excel
            ファイル（.xlsx）をアップロードしてください。読み込み後、登録前に内容を確認・修正できます。決済は確認画面で「請求する」を押した時点で行われます。
          </p>

          <a
            className="bulk-special-fee__template-link"
            href={TEMPLATE_URL}
            download="手数料一括インポート_テンプレート.xlsx"
          >
            <DownloadOutlined /> テンプレートをダウンロード
          </a>

          <Upload.Dragger
            className="bulk-special-fee__dragger"
            accept=".xlsx"
            maxCount={1}
            multiple={false}
            fileList={uploadFile ? [uploadFile] : []}
            beforeUpload={() => false}
            onChange={({ fileList }) => setUploadFile(fileList.slice(-1)[0] ?? null)}
            onRemove={() => {
              setUploadFile(null);
              return true;
            }}
          >
            <p className="bulk-special-fee__dragger-icon">
              <FileExcelOutlined />
            </p>
            <p className="bulk-special-fee__dragger-text">クリックまたはドラッグ＆ドロップでファイルを選択</p>
            <p className="bulk-special-fee__dragger-sub">.xlsx・最大100行・2MBまで</p>
          </Upload.Dragger>

          <div className="bulk-special-fee__footer">
            <CustomCommonButton
              isCancelButton={true}
              buttonProps={{ className: 'button-small', onClick: handleClose, disabled: importMutation.isPending }}
              content={intl.formatMessage({ id: 'common.cancel' })}
            />
            <CustomCommonButton
              buttonProps={{
                className: 'button-small',
                type: 'primary',
                onClick: handleImport,
                disabled: !uploadFile || importMutation.isPending,
                loading: importMutation.isPending,
              }}
              content="読み込む"
            />
          </div>
        </>
      )}

      {stage === 'edit' && (
        <>
          <p className="bulk-special-fee__title">{modalTitle}</p>
          {isImport && importSummary ? (
            <div className={`bulk-special-fee__import-banner ${importSummary.errorRowCount ? 'has-error' : ''}`}>
              <span>
                {importSummary.total}行を読み込みました。
                {importSummary.errorRowCount > 0
                  ? `うち ${importSummary.errorRowCount}行に修正が必要です（赤枠の項目）。`
                  : '内容を確認して「追加」を押してください。'}
              </span>
              <button type="button" className="bulk-special-fee__link-btn" onClick={() => setStage('upload')}>
                ファイルを選び直す
              </button>
            </div>
          ) : (
            <p className="bulk-special-fee__hint">
              1行 = 1商品 1手数料です。同じ商品に複数の手数料を付ける場合は、行を複製して手数料名を変更してください。
            </p>
          )}

          <Table
            className="bulk-special-fee__table bulk-special-fee__table--rows"
            size="small"
            rowKey="key"
            pagination={false}
            columns={editColumns as any}
            dataSource={rows}
            scroll={{ y: 380 }}
          />

          <div className="bulk-special-fee__toolbar">
            <button type="button" className="bulk-special-fee__add-line" onClick={addRow}>
              ＋ 行追加
            </button>
            <span className="bulk-special-fee__summary">{summaryText}</span>
          </div>

          <div className="bulk-special-fee__footer">
            <CustomCommonButton
              isCancelButton={true}
              buttonProps={{ className: 'button-small', onClick: handleClose }}
              content={intl.formatMessage({ id: 'common.cancel' })}
            />
            <CustomCommonButton
              buttonProps={{ className: 'button-small', type: 'primary', onClick: handleGoConfirm }}
              content={intl.formatMessage({ id: 'common.add' })}
            />
          </div>
        </>
      )}

      {stage === 'confirm' && (
        <>
          <p className="bulk-special-fee__title">{modalTitle}の確認</p>
          <p className="bulk-special-fee__confirm-note">
            以下の内容で即時決済を行います（{rows.length}件の決済）。決済後の取り消しはできません。
          </p>
          <Table
            className="bulk-special-fee__table"
            size="small"
            rowKey="key"
            pagination={false}
            columns={confirmColumns as any}
            dataSource={rows}
            scroll={{ y: 320 }}
          />
          <div className="bulk-special-fee__summary bulk-special-fee__summary--block">{summaryText}</div>

          <div className="bulk-special-fee__footer">
            <CustomCommonButton
              isCancelButton={true}
              buttonProps={{
                className: 'button-small',
                onClick: () => setStage('edit'),
                disabled: bulkMutation.isPending,
              }}
              content="戻る"
            />
            <CustomCommonButton
              buttonProps={{
                className: 'button-small',
                type: 'primary',
                onClick: handleSubmit,
                disabled: bulkMutation.isPending,
                loading: bulkMutation.isPending,
              }}
              content="請求する"
            />
          </div>
        </>
      )}

      {stage === 'result' && result && (
        <>
          <p className="bulk-special-fee__title">{modalTitle}の結果</p>
          <div className="bulk-special-fee__result-summary">
            <span>合計 {result.total}件</span>
            <span className="is-ok">成功 {result.succeeded}件</span>
            <span className={result.failed > 0 ? 'is-ng' : ''}>失敗 {result.failed}件</span>
          </div>
          {result.failed > 0 && (
            <p className="bulk-special-fee__result-note">
              失敗した行は決済されていません。原因を確認のうえ、該当商品で再度登録してください。
            </p>
          )}
          <Table
            className="bulk-special-fee__table"
            size="small"
            rowKey={(r: BulkSpecialHandlingFeeResultRow, idx) => `${r.storageItemId}-${idx}`}
            pagination={false}
            columns={resultColumns as any}
            dataSource={result.results}
            scroll={{ y: 360 }}
          />
          <div className="bulk-special-fee__footer">
            <CustomCommonButton
              buttonProps={{ className: 'button-small', type: 'primary', onClick: onClose }}
              content="閉じる"
            />
          </div>
        </>
      )}
    </Modal>
  );
};

export default BulkSpecialFeeModal;
